// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Sidebar from './Sidebar';
import WhatsNewModal from './WhatsNewModal';
import NommerCabinetModal from './NommerCabinetModal';
import AcceptationCgv from './AcceptationCgv';
import SuiviAnalysesBanner from './SuiviAnalysesBanner';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';

// Bandeau de fin de droits — licence suspendue ou expirée, abonnement résilié.
// `access_warning` est calculé par le serveur (`_etat_droits_compte`) et renvoyé
// aussi bien au login que par /api/auth/me : le bandeau survit donc au
// rechargement de page. Il est placé ICI, dans la coque commune, pour être vu
// quelle que soit la page ouverte — un client qui ne comprend pas pourquoi ses
// analyses sont refusées ouvre rarement la bonne page pour l'apprendre.
const AccessWarningBanner = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const warning = user?.access_warning;
  if (!warning) return null;

  // `bloquant` distingue la fin de droit (rouge, plus aucune analyse possible)
  // du simple avertissement de paiement en échec (ambre, accès conservé).
  const bloquant = warning.bloquant;
  const couleur = bloquant ? '#dc2626' : '#d97706';
  const fond = bloquant ? 'rgba(220,38,38,0.08)' : 'rgba(217,119,6,0.08)';

  // `message` est le repli lisible fourni par l'API si le code est inconnu du
  // catalogue (version de frontend antérieure à un nouveau motif de blocage).
  const cle = `access_warning_${warning.code}`;
  let texte = t(cle) === cle ? warning.message : t(cle);

  // Le préavis d'expiration porte une date. Elle arrive en `YYYY-MM-DD` (le
  // format d'un `<input type="date">`) et se lit mal telle quelle en français :
  // on la formate selon la locale du navigateur, comme les noms de mois de
  // `ConsommationsClient`. Donnée formatée, pas texte d'interface.
  if (warning.date_fin) {
    const [a, m, j] = warning.date_fin.split('-');
    const lisible = new Date(Number(a), Number(m) - 1, Number(j)).toLocaleDateString();
    texte = texte.replace('{date}', lisible);
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 20px', background: fond,
      borderBottom: `1px solid ${couleur}`, color: couleur,
      fontSize: 13, lineHeight: 1.4,
    }}>
      <AlertTriangle size={16} style={{ flexShrink: 0 }} />
      <span><strong>{t('access_warning_title')} — </strong>{texte}</span>
    </div>
  );
};

const AppLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'white' }}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(c => !c)}
      />
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <AccessWarningBanner />
        {/* Analyses qui tournent sans onglet pour les suivre, ou terminées pas
            encore vues — et l'avertissement à la fermeture (chantier ⑮). */}
        <SuiviAnalysesBanner />
        {children}
      </div>
      {/* Pop-up « Nouveautés » — placé ICI pour la même raison que le bandeau
          ci-dessus : la coque commune est le seul endroit traversé quelle que
          soit la page ouverte au moment de la connexion. Il se décide seul
          (version vue en `localStorage` — préférence d'APPAREIL, cf
          lib/stockageSession.js) et ne rend rien s'il n'y a rien à dire. */}
      <WhatsNewModal />
      {/* Titulaire d'un Cabinet sans nom : même raison d'être ici, et elle
          passe au-dessus de tout le reste (z-index) — cf le composant. */}
      <NommerCabinetModal />
      {/* CGU/CGV : une nouvelle version est entrée en vigueur pendant la session
          (relue par /api/auth/me toutes les 60 s). Passe au-dessus du reste. */}
      <AcceptationCgv />
    </div>
  );
};

export default AppLayout;
